import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getAllAdvances, addAdvance, getSupervisors, getAllApiariesService, deleteAdvance } from '../../../../services/apiaryService';
import close from '../assets/images/close.png';
import * as Styles from '../Farmers/components/FarmerForm/style';
import { Form, Row, Col, Button, Badge, Card } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import Select from 'react-select';
import GenericExportCSVModal from '../shared/GenericExportCSVModal';
import useEmployeeIds from './useEmployeeIds';

const AdvancesContainer = styled.div`
    padding: ${props => props.isGlobal ? '30px' : '20px 0'};
    background: ${props => props.isGlobal ? '#f8f9fa' : 'transparent'};
    min-height: ${props => props.isGlobal ? '100vh' : 'auto'};
    margin-left: ${props => props.isGlobal ? '15%' : '0'};
    width: ${props => props.isGlobal ? '85%' : '100%'};

    @media (max-width: 500px) {
        margin-left: 0;
        width: 100%;
        padding: 15px;
    }
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
`;

const Title = styled.h2`
    color: #2d3436;
    font-weight: 700;
    margin: 0;
`;

const AddAdvanceModal = ({ show, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        issued_to: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        type: 'ADVANCE',
        comments: '',
        apiary_id: ''
    });
    const [supervisors, setSupervisors] = useState([]);
    const [apiaries, setApiaries] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const empIdFor = useEmployeeIds();

    useEffect(() => {
        if (show) {
            getSupervisors().then(setSupervisors);
            getAllApiariesService().then(setApiaries);
        }
    }, [show]);

    // "Name (EMP ID)" label, searchable by name, employee ID or role.
    const personnelOptions = supervisors.map(sv => {
        const emp = empIdFor(sv.id);
        return {
            value: sv.id,
            label: `${sv.name}${emp ? ` (${emp})` : ''} - ${sv.role}`,
            searchText: `${sv.name || ''} ${emp} ${sv.role || ''}`.toLowerCase(),
        };
    });

    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.issued_to) {
            toast.error("Please select a supervisor!");
            return;
        }
        setIsSubmitting(true);
        try {
            await addAdvance({ ...formData, amount: Number(formData.amount) });
            toast.success("Entry added successfully!");
            onSuccess();
            onClose();
        } catch (error) {
            toast.error("Failed to add entry");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Styles.ModalWrapper style={{ zIndex: 1050 }}>
            <Styles.ModalContent2 style={{ maxWidth: '550px' }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "25px" }}>
                    <h3 style={{ color: '#00b894', margin: 0, fontWeight: 700 }}>Issue Advance / Reimbursement</h3>
                    <img src={close} alt="Close" onClick={onClose} style={{ width: 20, cursor: "pointer", opacity: 0.6 }} />
                </div>
                <Form onSubmit={handleSubmit} style={{ padding: '0 25px 25px 25px' }}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Type</Form.Label>
                                <Form.Control as="select" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                                    <option value="ADVANCE">Advance</option>
                                    <option value="REIMBURSEMENT">Reimbursement</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Date</Form.Label>
                                <Form.Control type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                            </Form.Group>
                        </Col>
                    </Row>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Issued To (Personnel)</Form.Label>
                        <Select
                            options={personnelOptions}
                            value={personnelOptions.find(o => String(o.value) === String(formData.issued_to)) || null}
                            onChange={opt => setFormData({...formData, issued_to: opt ? opt.value : ''})}
                            filterOption={(option, input) => option.data.searchText.includes((input || '').trim().toLowerCase())}
                            placeholder="Search by name or employee ID"
                            isClearable
                            maxMenuHeight={220}
                            menuPortalTarget={document.body}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Amount (₹)</Form.Label>
                        <Form.Control type="number" placeholder="0.00" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} required />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Link to Apiary (Optional)</Form.Label>
                        <Form.Control 
                            as="select" 
                            value={formData.apiary_id} 
                            onChange={e => setFormData({...formData, apiary_id: e.target.value})}
                        >
                            <option value="">-- Not Linked / Independent --</option>
                            {apiaries.map(api => (
                                <option key={api.apiary_id} value={api.apiary_id}>
                                    {api.apiary_name} ({api.apiary_id_display || api.apiary_id})
                                </option>
                            ))}
                        </Form.Control>
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label>Comments / UTR No.</Form.Label>
                        <Form.Control as="textarea" rows={3} value={formData.comments} onChange={e => setFormData({...formData, comments: e.target.value})} placeholder="Reference details..." />
                    </Form.Group>

                    <div className="text-right d-flex gap-2 justify-content-end">
                        <Button variant="light" onClick={onClose}>Cancel</Button>
                        <Button variant="success" type="submit" disabled={isSubmitting} style={{ background: '#00b894', border: 'none', padding: '10px 25px' }}>
                            {isSubmitting ? 'Saving...' : 'Save Entry'}
                        </Button>
                    </div>
                </Form>
            </Styles.ModalContent2>
        </Styles.ModalWrapper>
    );
};

const AdvancesTable = ({ isGlobal = true, isAdmin = true } = {}) => {
    const [advances, setAdvances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [apiaries, setApiaries] = useState([]);
    const [page, setPage] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(1000);
    const empIdFor = useEmployeeIds();

    const paginatedAdvances = useMemo(() => {
        const start = (page - 1) * sizePerPage;
        return advances.slice(start, start + sizePerPage);
    }, [advances, page, sizePerPage]);

    const totalPages = Math.ceil(advances.length / sizePerPage) || 1;

    const fetchData = async () => {
        setLoading(true);
        try {
            const [advData, apiData] = await Promise.all([
                getAllAdvances(),
                getAllApiariesService()
            ]);
            setAdvances(advData);
            setApiaries(apiData);
        } catch (error) {
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDelete = (row) => {
        Swal.fire({
            title: 'Delete Entry?',
            text: `Are you sure you want to delete ${row.advance_id_display}? Any associated expense allocations will also be deleted, and those expenses will become unsettled.`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e74c3c',
            cancelButtonColor: '#95a5a6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.value) {
                try {
                    await deleteAdvance(row._id);
                    toast.success("Entry deleted successfully!");
                    fetchData();
                } catch (error) {
                    const errMsg = error.response?.data?.error || "Failed to delete entry";
                    toast.error(errMsg);
                }
            }
        });
    };

    const EXPORT_COLUMNS = [
        { label: 'ID', key: 'id', path: 'advance_id_display' },
        { label: 'Date', key: 'date', formatter: (row) => row.date ? new Date(row.date).toLocaleDateString('en-IN') : '' },
        { label: 'Type', key: 'type', path: 'type' },
        { label: 'Status', key: 'status', formatter: (row) => row.status || 'ACTIVE' },
        { label: 'Issued To', key: 'issued_to_name', formatter: (row) => row.issued_to_name || 'Staff' },
        { label: 'Employee ID', key: 'employee_id', formatter: (row) => row.user_employee_id || row.employee_id || row.issued_to_employee_id || row.paid_by_employee_id || empIdFor(row.issued_to) || '' },
        {
            label: 'Linked Apiary',
            key: 'apiary',
            formatter: (row) => {
                if (!row.apiary_id) return 'Independent';
                const api = apiaries.find(a => a.apiary_id === row.apiary_id || a._id === row.apiary_id);
                return api ? api.apiary_name : row.apiary_id;
            }
        },
        { label: 'Total Amount', key: 'amount', formatter: (row) => row.amount },
        { label: 'Amount Left', key: 'amount_left', formatter: (row) => row.amount_left },
        { label: 'Comments', key: 'comments', path: 'comments' }
    ];

    const columns = [
        {
            dataField: 'advance_id_display',
            text: 'ID',
            sort: true,
            formatter: (cell) => <strong style={{ color: '#636e72' }}>{cell}</strong>
        },
        {
            dataField: 'date',
            text: 'Date',
            sort: true,
            formatter: (cell) => new Date(cell).toLocaleDateString('en-IN')
        },
        {
            dataField: 'type',
            text: 'Type',
            sort: true,
            formatter: (cell) => (
                <Badge bg={cell === 'ADVANCE' ? 'info' : 'warning'} style={{ 
                    padding: '6px 10px', 
                    borderRadius: '4px',
                    fontSize: '10px',
                    background: cell === 'ADVANCE' ? '#e1f5fe' : '#fff3e0',
                    color: cell === 'ADVANCE' ? '#0288d1' : '#f57c00',
                    border: `1px solid ${cell === 'ADVANCE' ? '#b3e5fc' : '#ffe0b2'}`
                }}>
                    {cell}
                </Badge>
            )
        },
        {
            dataField: 'status',
            text: 'Status',
            sort: true,
            formatter: (cell) => (
                <Badge bg={cell === 'SETTLED' ? 'success' : 'primary'} style={{ 
                    padding: '6px 10px', 
                    borderRadius: '4px',
                    fontSize: '10px',
                    background: cell === 'SETTLED' ? '#e8f8f5' : '#e3f2fd',
                    color: cell === 'SETTLED' ? '#00b894' : '#1976d2',
                    border: `1px solid ${cell === 'SETTLED' ? '#c3e6cb' : '#bbdefb'}`
                }}>
                    {cell || 'ACTIVE'}
                </Badge>
            )
        },
        {
            dataField: 'issued_to_name',
            text: 'Issued To',
            sort: true,
            formatter: (cell) => (
                <span style={{ background: '#f1f2f6', padding: '3px 7px', borderRadius: '4px', fontSize: '13px', width: 'fit-content' }}>
                    {cell || 'Staff'}
                </span>
            )
        },
        {
            dataField: 'employee_id_col',
            text: 'Employee ID',
            formatter: (cell, row) => {
                const empId = row.user_employee_id || row.employee_id || row.issued_to_employee_id || row.paid_by_employee_id || empIdFor(row.issued_to);
                if (!empId) return <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>;
                return (
                    <span style={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '3px 8px', borderRadius: '4px', border: '1px solid #bfdbfe' }}>
                        {empId}
                    </span>
                );
            }
        },
        {
            dataField: 'apiary_id',
            text: 'Linked Apiary',
            formatter: (cell) => {
                if (!cell) return <span style={{ color: '#b2bec3', fontStyle: 'italic' }}>Independent</span>;
                const api = apiaries.find(a => a.apiary_id === cell || a._id === cell);
                return (
                    <a 
                        href={`/nector-apiaries/${api ? api.apiary_id : cell}`} 
                        style={{ color: '#0984e3', textDecoration: 'underline', fontWeight: 500 }}
                        title="Go to Apiary"
                    >
                        {api ? api.apiary_name : cell}
                    </a>
                );
            }
        },
        {
            dataField: 'amount',
            text: 'Total Amount',
            sort: true,
            formatter: (cell) => <span style={{ fontWeight: 700, color: '#0984e3' }}>₹ {cell.toLocaleString()}</span>
        },
        {
            dataField: 'amount_left',
            text: 'Amount Left',
            sort: true,
            formatter: (cell) => (
                <span style={{ 
                    fontWeight: 700, 
                    color: cell > 0 ? '#00b894' : '#636e72',
                    padding: '4px 8px',
                    background: cell > 0 ? '#e8f8f5' : '#f1f2f6',
                    borderRadius: '4px'
                }}>
                    ₹ {cell.toLocaleString()}
                </span>
            )
        },
        {
            dataField: 'comments',
            text: 'Comments',
            formatter: (cell) => <span style={{ fontSize: '12px', color: '#636e72' }}>{cell || '-'}</span>
        },
        {
            dataField: 'actions',
            text: 'Actions',
            isDummyField: true,
            formatter: (cell, row) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    {isAdmin ? (
                        <Button 
                            variant="link" 
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row);
                            }}
                            style={{ color: '#e74c3c', padding: '0', fontSize: '16px' }}
                            title="Delete Entry"
                        >
                            <i className="fa fa-trash"></i>
                        </Button>
                    ) : (
                        <span style={{ color: '#b2bec3', fontSize: '12px' }}>—</span>
                    )}
                </div>
            )
        }
    ];

    return (
        <AdvancesContainer isGlobal={isGlobal}>
            <HeaderSection>
                <div>
                    <Title>Advances & Reimbursements</Title>
                    <p style={{ color: '#636e72', marginTop: '5px' }}>Manage staff payments and track settlements independently or by apiary.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowExportModal(true)}
                        style={{ padding: '10px 20px', fontWeight: 600 }}
                    >
                        <i className="fa fa-download mr-2"></i> Export CSV
                    </Button>
                    {isAdmin && (
                        <Button
                            onClick={() => setShowAddModal(true)}
                            style={{ background: '#00b894', border: 'none', padding: '10px 20px', fontWeight: 600 }}
                        >
                            <i className="fa fa-plus mr-2"></i> Add Entry
                        </Button>
                    )}
                </div>
            </HeaderSection>

            <Card style={{ border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>
                <Card.Body style={{ padding: '0' }}>
                    <div className="custom-table-container">
                        <BootstrapTable
                            keyField="_id"
                            data={paginatedAdvances}
                            columns={columns}
                            loading={loading}
                            bordered={false}
                            hover
                            noDataIndication={() => (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#b2bec3' }}>
                                    {loading ? 'Loading data...' : 'No entries found.'}
                                </div>
                            )}
                            headerClasses="custom-table-header"
                        />
                    </div>
                </Card.Body>
            </Card>

            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <span>
                        <strong>
                            Page {page} of {totalPages}
                        </strong>
                    </span>
                    <select
                        value={sizePerPage}
                        onChange={e => {
                            setSizePerPage(Number(e.target.value));
                            setPage(1);
                        }}
                        style={{
                            padding: '5px 10px',
                            borderRadius: '4px',
                            border: '1px solid #d9d9d9',
                            cursor: 'pointer',
                            fontSize: '14px',
                        }}
                    >
                        {[5, 10, 25, 50, 100, 500, 1000, 2000, 3000, 5000].map(pageSizeOption => (
                            <option key={pageSizeOption} value={pageSizeOption}>
                                Show {pageSizeOption}
                            </option>
                        ))}
                    </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                    <button
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                    >
                        {'<<'}
                    </button>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                    >
                        Previous
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            type="number"
                            value={page}
                            min={1}
                            max={totalPages}
                            onChange={e => {
                                const val = Number(e.target.value) || 1;
                                setPage(Math.min(totalPages, Math.max(1, val)));
                            }}
                            style={{ width: '45px', textAlign: 'center', padding: '4px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                        />
                    </div>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                    >
                        Next
                    </button>
                    <button
                        onClick={() => setPage(totalPages)}
                        disabled={page >= totalPages}
                        style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                    >
                        {'>>'}
                    </button>
                </div>
            </div>

            <AddAdvanceModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={fetchData}
            />

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={advances}
                columns={EXPORT_COLUMNS}
                title="Export Advances to CSV"
                filenamePrefix="advances_export"
            />

            <style>{`
                .custom-table-header {
                    background: #fdfdfd;
                    border-bottom: 2px solid #f1f2f6 !important;
                }
                .custom-table-header th {
                    color: #636e72 !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    padding: 15px 20px !important;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .react-bootstrap-table td {
                    padding: 15px 20px !important;
                    vertical-align: middle !important;
                    font-size: 14px;
                    color: #2d3436;
                    border-top: 1px solid #f1f2f6 !important;
                }
                .react-bootstrap-table tr:hover {
                    background-color: #f8f9fa !important;
                }
                .page-item.active .page-link {
                    background-color: #00b894 !important;
                    border-color: #00b894 !important;
                }
                .page-link {
                    color: #00b894 !important;
                }
            `}</style>
        </AdvancesContainer>
    );
};

export default AdvancesTable;
